// Copyright Caproc Oy
// System name: Aqua-Net 
// System module: Sensor
// Author: TS
//
// 19.3.2020 TS Initial version
// 28.7.2020 TS Added alarm_notification_status
// 28.4.2021 TS Added email and sms alarm settings
// 7.1.2024 1.0.8 TS Polygon texts and titles
// 7.4.2026 TS (claude) Added Polygon-specific Description placeholder explaining WDR/TCS
// 7.4.2026 TS (claude) Added Google Maps location preview that updates with the latitude/longitude fields
// 7.4.2026 TS (claude) Disabled Enter-key form submit; Save now requires explicit button click
// 7.4.2026 TS (claude) Fixed eslint warning: removed unused 'is_sms_notifications_enabled' destructure
// 8.4.2026 TS (claude) Constrained form max width to 900px so inputs and dividers don't span the wider desktop layout

import React from "react";
import { Form, Input, Message, Header, Button, Checkbox, Select } from "semantic-ui-react";
import Shell from "./Shell";
import { authAxios } from "../../utils";
import { setActivity } from "../../store/actions/auth";
import { withRouter, Redirect } from "react-router-dom";
import { connect } from "react-redux";
import { withTranslation } from 'react-i18next';
import { sensorGroupDetailsURL, updateSensorGroupDetailsURL, createSensorGroupDetailsURL, countriesURL } from "../../constants";
import AddressAutocomplete from "./AddressAutocomplete";
import { GoogleMap, withScriptjs, withGoogleMap, Marker } from "react-google-maps";

const LocationPreviewMap = withScriptjs(withGoogleMap(props => (
    <GoogleMap
        zoom={15}
        center={{ lat: props.lat, lng: props.lng }}>
        <Marker position={{ lat: props.lat, lng: props.lng }} />
    </GoogleMap>
)));


class SensorGroupDetails extends React.Component {
    state = {
        company: "",
        name: "",
        description: "",
        alarm_notification_status: "A",
        sensor_group_id: null,
        is_email_notifications_enabled: true,
        is_sms_notifications_enabled: false,
        error: null,
        success_mgs: null,
        loading: false,
        create: null,
        default_group: false,
        location_address: "",
        location_postal_code: "",
        location_city: "",
        location_country: "fi",
        location_longitude: "0",
        location_latitude: "0",
        location_altitude: "0",
        country_options: null
    };

    componentDidMount() {
        this.props.setActivity();
        this.handleFetchCountries();
        if (this.props.match.params.sensor_group_id === "create") {
            this.setState({
                create: true,
                is_active: true
            })
        } else {
            this.handleDetails();
        }
    }

    handleFetchCountries = () => {
        authAxios
            .get(countriesURL)
            .then(res => {
                this.setState({
                    country_options: this.handleFormatCountries(res.data)
                });
            })
            .catch(err => { });
    };

    handleFormatCountries = countries => {
        const keys = Object.keys(countries);
        return keys.map(k => {
            return {
                key: k,
                text: countries[k],
                value: k
            };
        });
    };

    handlePlaceSelected = (placeData) => {
        this.setState({
            location_address: placeData.address,
            location_postal_code: placeData.postal_code,
            location_city: placeData.city,
            location_country: placeData.country,
            location_latitude: placeData.latitude.toString(),
            location_longitude: placeData.longitude.toString(),
            success_mgs: null,
            error: null
        });
    };

    handleGeocode = (coords) => {
        this.setState({
            location_latitude: coords.latitude.toString(),
            location_longitude: coords.longitude.toString(),
            success_mgs: null,
            error: null
        });
    };

    handleSelectChange = (e, { name, value }) => {
        this.setState({
            [name]: value
        });
    };

    handleDetails = () => {
        this.setState({
            loading: true
        });
        const { activeCompanyId } = this.props;
        const { sensor_group_id } = this.props.match.params;
        authAxios
            .get(sensorGroupDetailsURL(activeCompanyId, sensor_group_id))
            .then(res => {
                this.setState({
                    sensor_group_id: res.data.id,
                    company: res.data.company,
                    name: res.data.name,
                    description: res.data.description,
                    alarm_notification_status: res.data.alarm_notification_status,
                    is_email_notifications_enabled: res.data.is_email_notifications_enabled,
                    is_sms_notifications_enabled: res.data.is_sms_notifications_enabled,
                    location_address: res.data.location_address || "",
                    location_postal_code: res.data.location_postal_code || "",
                    location_city: res.data.location_city || "",
                    location_country: res.data.location_country || "fi",
                    location_longitude: res.data.location_longitude != null ? res.data.location_longitude.toString() : "0",
                    location_latitude: res.data.location_latitude != null ? res.data.location_latitude.toString() : "0",
                    location_altitude: res.data.location_altitude != null ? res.data.location_altitude.toString() : "0",
                    error: null,
                    success_mgs: null,
                    loading: false,
                    default_group: res.data.name === "Default group" ? true : false
                });
            })
            .catch(err => {
                this.setState({
                    loading: false,
                    success_mgs: null,
                    error: err
                });
            });
    };

    handleChange = e => {
        this.setState({
            [e.target.name]: e.target.value,
            success_mgs: null,
            error: null
        });
    };

    handleCheckboxChange = (e, { name, checked }) => {
        
        this.setState({
            [name]: checked,
            success_mgs: null,
            error: null,
        });
    };

    handleSubmit = e => {
        if (e && e.preventDefault) e.preventDefault();
        this.setState({
            loading: true
        });
        const {
            company,
            name,
            description,
            alarm_notification_status,
            is_email_notifications_enabled,
            is_sms_notifications_enabled,
            sensor_group_id,
            create,
            location_address,
            location_postal_code,
            location_city,
            location_country,
            location_longitude,
            location_latitude,
            location_altitude
        } = this.state;
        const { activeCompanyId, t } = this.props;
        if (name === "") {
            this.setState({
                error: t("Please fill in name"),
                success_mgs: null,
                loading: false
            });
        } else if (name.length > 50) {
            this.setState({
                error: t("Max amount of characters is 50 for Name"),
                success_mgs: null,
                loading: false
            });
        } else if (location_address.length > 150) {
            this.setState({
                error: t("Max amount of characters is 150 for Address"),
                success_mgs: null,
                loading: false
            });
        } else if (location_postal_code.length > 50) {
            this.setState({
                error: t("Max amount of characters is 50 for Postal code"),
                success_mgs: null,
                loading: false
            });
        } else if (location_city.length > 50) {
            this.setState({
                error: t("Max amount of characters is 50 for City"),
                success_mgs: null,
                loading: false
            });
        } else {
            const locationData = {
                location_address,
                location_postal_code,
                location_city,
                location_country,
                location_longitude: parseFloat(location_longitude) || 0,
                location_latitude: parseFloat(location_latitude) || 0,
                location_altitude: parseFloat(location_altitude) || 0
            };
            if (create) {
                authAxios
                    .post(createSensorGroupDetailsURL(activeCompanyId), {
                        company: activeCompanyId,
                        name,
                        description,
                        alarm_notification_status,
                        is_email_notifications_enabled,
                        is_sms_notifications_enabled,
                        author: localStorage.getItem("UserID"),
                        ...locationData
                    })
                    .then(res => {
                        this.props.history.push(`/sensor/sensor-groups`)
                    })
                    .catch(err => {
                        this.setState({
                            loading: false,
                            success_mgs: null,
                            error: err
                        });
                    });
            } else {
                authAxios
                    .put(updateSensorGroupDetailsURL(activeCompanyId, sensor_group_id), {
                        company,
                        name,
                        description,
                        alarm_notification_status,
                        is_email_notifications_enabled,
                        is_sms_notifications_enabled,
                        ...locationData
                    })
                    .then(res => {
                        this.props.history.push(`/sensor/sensor-groups`)
                    })
                    .catch(err => {
                        this.setState({
                            loading: false,
                            success_mgs: null,
                            error: err
                        });
                    });
            }
        }
    };

    render() {
        const {
            name,
            description,
            is_email_notifications_enabled,
            location_address,
            location_postal_code,
            location_city,
            location_country,
            location_longitude,
            location_latitude,
            country_options,
            error,
            success_mgs,
            loading,
            create
        } = this.state;
        const { isAuthenticated, t } = this.props;
        if (!isAuthenticated) {
            return <Redirect to="/login" />;
        }
        // console.log(this.state);
        const logo = localStorage.getItem("Logo");
        return (
            <Shell>
                {logo && (
                    <>
                        {logo === "Polygon" ? (
                            <Header as="h4">{t("Kohteen tiedot")}</Header>
                        ) : (
                            <Header as="h4">{t("Sensor group details")}</Header>
                        )}
                        <Form error={error !== null} style={{ maxWidth: 900 }}>
                            <Form.Field>
                                <label>{t("Name")}</label>
                                {logo === "Polygon" ? (
                                    <Input
                                        disabled={this.state.default_group}
                                        value={name === "Default group" ? "TMP VARASTO" : name}
                                        name="name"
                                        onChange={this.handleChange} />
                                ) : (
                                    <Input
                                        disabled={this.state.default_group}
                                        value={name}
                                        name="name"
                                        onChange={this.handleChange} />
                                )}
                            </Form.Field>
                            <Form.Field>
                                <label>{t("Description")}</label>
                                <Input
                                    value={description}
                                    name="description"
                                    placeholder={logo === "Polygon" ? t("Kerro kuivattava rakenne (WDR) / mitattava rakenne (TCS)") : ""}
                                    onChange={this.handleChange} />
                            </Form.Field>
                            <Form.Field>
                                <Checkbox
                                    label={<label>{t("Email alarms enabled")}</label>}
                                    name="is_email_notifications_enabled"
                                    onChange={this.handleCheckboxChange}
                                    checked={is_email_notifications_enabled}
                                />
                            </Form.Field>
                            {/* <Form.Field>
                                <Checkbox
                                    disabled={true}
                                    label={<label>{t("SMS alarms enabled")}</label>}

                                    name="is_sms_notifications_enabled"
                                    onChange={this.handleCheckboxChange}
                                    checked={is_sms_notifications_enabled}
                                />
                            </Form.Field> */}

                            <Header as="h5">{t("Location address details")}</Header>
                            <hr/>
                            <Form.Field width="8">
                                <label>{t("Address")}</label>
                                <AddressAutocomplete
                                    value={location_address}
                                    name="location_address"
                                    onChange={this.handleChange}
                                    onPlaceSelected={this.handlePlaceSelected}
                                    onGeocode={this.handleGeocode}
                                    address={location_address}
                                    postalCode={location_postal_code}
                                    city={location_city}
                                    t={t}
                                    showGeocodeButton={true}
                                />
                            </Form.Field>
                            <Form.Field width="3">
                                <label>{t("Postal code")}</label>
                                <Input
                                    value={location_postal_code}
                                    name="location_postal_code"
                                    onChange={this.handleChange} />
                            </Form.Field>
                            <Form.Field width="5">
                                <label>{t("City")}</label>
                                <Input
                                    value={location_city}
                                    name="location_city"
                                    onChange={this.handleChange} />
                            </Form.Field>
                            {country_options && (
                                <Form.Field width="5">
                                    <label>{t("Country")}</label>
                                    <Select
                                        search
                                        name='location_country'
                                        options={country_options}
                                        placeholder={location_country}
                                        onChange={this.handleSelectChange}
                                        value={location_country}
                                    />
                                </Form.Field>
                            )}
                            <Form.Field width="3">
                                <label>{t("Latitude")}</label>
                                <Input
                                    value={location_latitude}
                                    name="location_latitude"
                                    onChange={this.handleChange} />
                            </Form.Field>
                            <Form.Field width="3">
                                <label>{t("Longitude")}</label>
                                <Input
                                    value={location_longitude}
                                    name="location_longitude"
                                    onChange={this.handleChange} />
                            </Form.Field>
                            {(() => {
                                const lat = parseFloat(location_latitude);
                                const lng = parseFloat(location_longitude);
                                if (isNaN(lat) || isNaN(lng)) return null;
                                return (
                                    <Form.Field width="16">
                                        <div style={{ width: "100%", height: "300px", marginTop: 10 }}>
                                            <LocationPreviewMap
                                                key={`${lat},${lng}`}
                                                lat={lat}
                                                lng={lng}
                                                googleMapURL={`https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_KEY}&callback=initMap`}
                                                loadingElement={<div style={{ height: `100%` }} />}
                                                containerElement={<div style={{ width: `100%`, height: `100%` }} />}
                                                mapElement={<div style={{ width: `100%`, height: `100%` }} />}
                                            />
                                        </div>
                                    </Form.Field>
                                );
                            })()}
                            <hr/>

                            {error && <Message error header={t("Error")} content={error} />}
                            {success_mgs && <Message positive header={success_mgs} />}
                            <Button primary type="button" loading={loading} disabled={loading} onClick={this.handleSubmit}>
                                {create ? (t("Create")) : (t("Save"))}
                            </Button>
                        </Form>
                    </>
                )}
            </Shell>
        );
    }
}
const mapStateToProps = state => {
    return {
        activeCompanyId: state.company.activeCompanyId,
        isAuthenticated: state.auth.token !== null
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setActivity: () => dispatch(setActivity())
    };
};

export default withRouter(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(withTranslation('sensorgroupdetails')(SensorGroupDetails))
);